// Planning (MRP) — data access (docs/design/planning.md).
//
//   status    rpc planning_run_status(company) — active run + latest finished (polled while a run is active).
//   run       rpc request_mrp_run(company) — queues a run; the pg_cron worker processes it.
//   list      rpc planned_order_page(company, status, kind, term, after_due, after_id, 51) —
//             keyset (due_date, id) on planned_orders_list_idx.
//   coverage  rpc item_coverage(company, item) — parameters, projection lines and exceptions of the
//             latest run (planning_lines primary key); one round-trip.
//   params    rpc item_planning_document / save_item_planning.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export type PlannedOrderStatus = "planned" | "firmed" | "converted";
export type PlannedOrderKind = "purchase" | "production" | "subcontract";
export type PlannedStatusFilter = "open" | "all" | PlannedOrderStatus;
export const plannedStatusFilters: PlannedStatusFilter[] = ["open", "planned", "firmed", "converted", "all"];
export const isPlannedStatusFilter = (v: unknown): v is PlannedStatusFilter => (plannedStatusFilters as unknown[]).includes(v);
export const isPlannedKind = (v: unknown): v is PlannedOrderKind => v === "purchase" || v === "production" || v === "subcontract";

export interface RunInfo {
  id: string;
  status: "queued" | "running" | "completed" | "failed";
  run_date?: string | null;
  horizon_days?: number;
  requested_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
  item_count?: number;
  planned_order_count?: number;
  exception_count?: number;
  requested_by_name?: string | null;
  /** Queued by the MRP schedule, not by a person. */
  scheduled?: boolean;
}

/** MRP schedule of a company (planning_schedules): local time, ISO weekdays (1 = Monday). */
export interface MrpSchedule {
  enabled: boolean;
  run_at: string;
  weekdays: number[];
  horizon_days: number;
  timezone?: string;
  next_run_at: string | null;
}

export interface RunStatus {
  active: RunInfo | null;
  last: RunInfo | null;
  /** A kit or order move after the last completed run (docs/design/aps.md K4). */
  stale?: boolean;
  plan_changed_at?: string | null;
  /** null until a schedule is saved. */
  schedule?: MrpSchedule | null;
}

export interface PlannedOrderRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  kind: PlannedOrderKind;
  status: PlannedOrderStatus;
  qty: string | number;
  base_uom_code: string;
  due_date: string;
  start_date: string;
  supplier_id: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  warehouse_code: string;
  converted_document_type: string | null;
  converted_document_id: string | null;
  start_in_past: boolean;
  /** Planned for the demand of this project (PR 4); converting pegs the new order. */
  project_id?: string | null;
  project_code?: string | null;
  /** Component coverage at the last MRP run (production only). */
  readiness?: PlannedReadiness | null;
  material_date?: string | null;
  short_components?: number;
}

export type PlannedReadiness = "ready" | "covered" | "waiting" | "late" | "short" | "no_bom";

export interface PlannedListParams {
  companyId: string;
  status: PlannedStatusFilter;
  kind: PlannedOrderKind | undefined;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface ItemPlanning {
  company_id: string;
  item_id: string;
  item_code: string;
  procurement_type: "purchase" | "manufacture";
  is_default: boolean;
  planning_method: "mrp" | "none";
  supplier_id: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  lead_time_days: number | null;
  effective_lead_time_days: number;
  safety_stock_qty: string | number;
  min_order_qty: string | number;
  order_multiple: string | number;
  fixed_order_qty: string | number;
  receipt_warehouse_id: string | null;
  receipt_warehouse_code: string | null;
  buyer_id?: string | null;
  buyer_name?: string | null;
  /** Made by the supplier from our material (item subcontracting, ADR-055). */
  subcontracted?: boolean;
  subcontract_warehouse_id?: string | null;
  subcontract_warehouse_code?: string | null;
}

export interface ItemPlanningInput {
  planning_method: "mrp" | "none";
  supplier_id: string | null;
  lead_time_days: number | null;
  safety_stock_qty: string;
  min_order_qty: string;
  order_multiple: string;
  fixed_order_qty: string;
  receipt_warehouse_id: string | null;
  buyer_id: string | null;
  subcontracted: boolean;
  subcontract_warehouse_id: string | null;
}

export type CoverageLineKind = "on_hand" | "demand" | "supply" | "planned";
export type ExceptionKind = "expedite" | "postpone" | "excess" | "start_in_past" | "demand_in_past" | "below_safety" | "no_bom";

export interface CoverageLine {
  seq: number;
  event_date: string;
  kind: CoverageLineKind;
  source_type: string | null;
  source_id: string | null;
  source_number: string | null;
  qty: string | number;
  balance: string | number;
  /** Planning segment (PR 4): project lines net only that project's stock and supply. */
  project_id?: string | null;
  project_code?: string | null;
}

export interface CoverageException {
  kind: ExceptionKind;
  source_type: string | null;
  source_id: string | null;
  source_number: string | null;
  need_date: string | null;
  supply_date: string | null;
  qty: string | number | null;
}

export interface ItemCoverage {
  parameters: ItemPlanning | null;
  base_uom_code: string | null;
  base_uom_decimals: number | null;
  run: { id: string; run_date: string; finished_at: string; horizon_days: number } | null;
  lines: CoverageLine[];
  exceptions: CoverageException[];
}

export const PLANNED_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

async function getRunStatus(companyId: string, signal: AbortSignal): Promise<RunStatus> {
  if (isDemoMode) return { active: null, last: null };
  const { data, error } = await db().rpc("planning_run_status", { p_company_id: companyId }).abortSignal(signal);
  if (error) throw error;
  return (data as RunStatus | null) ?? { active: null, last: null };
}

export async function requestRun(companyId: string): Promise<RunInfo> {
  if (isDemoMode) throw new Error("MRP is not available in demo mode");
  const { data, error } = await db().rpc("request_mrp_run", { p_company_id: companyId });
  if (error) throw error;
  return data as RunInfo;
}

export async function saveSchedule(companyId: string, schedule: Partial<Omit<MrpSchedule, "next_run_at" | "timezone">>): Promise<MrpSchedule> {
  if (isDemoMode) throw new Error("MRP is not available in demo mode");
  const { data, error } = await db().rpc("save_planning_schedule", { p_company_id: companyId, p_schedule: schedule });
  if (error) throw error;
  return data as MrpSchedule;
}

export async function listPlannedOrders(
  p: PlannedListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = PLANNED_PAGE,
): Promise<Page<PlannedOrderRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("planned_order_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_kind: p.kind ?? null,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (PlannedOrderRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getCoverage(companyId: string, itemId: string, signal: AbortSignal): Promise<ItemCoverage | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("item_coverage", { p_company_id: companyId, p_item_id: itemId }).abortSignal(signal);
  if (error) throw error;
  return (data as ItemCoverage | null) ?? null;
}

async function getItemPlanning(companyId: string, itemId: string, signal: AbortSignal): Promise<ItemPlanning | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("item_planning_document", { p_company_id: companyId, p_item_id: itemId }).abortSignal(signal);
  if (error) throw error;
  return (data as ItemPlanning | null) ?? null;
}

export async function saveItemPlanning(companyId: string, itemId: string, input: ItemPlanningInput): Promise<ItemPlanning> {
  if (isDemoMode) throw new Error("Planning is not available in demo mode");
  const { data, error } = await db().rpc("save_item_planning", { p_company_id: companyId, p_item_id: itemId, p_params: input });
  if (error) throw error;
  return data as ItemPlanning;
}

export interface Buyer {
  user_id: string;
  name: string;
}

/** Active members of the organization (buyer picker / filter). */
export const buyersQuery = queryOptions({
  queryKey: ["planning", "buyers"] as const,
  queryFn: async ({ signal }): Promise<Buyer[]> => {
    if (isDemoMode) return [];
    const { data, error } = await db().rpc("planning_buyers").abortSignal(signal);
    if (error) throw error;
    return (data ?? []) as Buyer[];
  },
  staleTime: 5 * 60_000,
});

export const planningKeys = {
  all: ["planning"] as const,
  status: (companyId: string) => [...planningKeys.all, "status", companyId] as const,
  planned: (p: PlannedListParams) => [...planningKeys.all, "planned", p] as const,
  coverage: (companyId: string, itemId: string) => [...planningKeys.all, "coverage", companyId, itemId] as const,
  params: (companyId: string, itemId: string) => [...planningKeys.all, "params", companyId, itemId] as const,
};

/** Polls every second while a run is queued or running. */
export function runStatusQuery(companyId: string) {
  return queryOptions({
    queryKey: planningKeys.status(companyId),
    queryFn: ({ signal }) => getRunStatus(companyId, signal),
    staleTime: 10_000,
    enabled: !!companyId,
    refetchInterval: (q) => (q.state.data?.active ? 1000 : false),
  });
}

export function plannedOrderListQuery(p: PlannedListParams) {
  return infiniteQueryOptions({
    queryKey: planningKeys.planned(p),
    queryFn: ({ pageParam, signal }) => listPlannedOrders(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

export function coverageQuery(companyId: string, itemId: string) {
  return queryOptions({
    queryKey: planningKeys.coverage(companyId, itemId),
    queryFn: ({ signal }) => getCoverage(companyId, itemId, signal),
    staleTime: 30_000,
    enabled: !!companyId && !!itemId,
  });
}

export function itemPlanningQuery(companyId: string, itemId: string) {
  return queryOptions({
    queryKey: planningKeys.params(companyId, itemId),
    queryFn: ({ signal }) => getItemPlanning(companyId, itemId, signal),
    staleTime: 60_000,
    enabled: !!companyId && !!itemId,
  });
}

const rules: [RegExp, string][] = [
  [/^Quantities allow at most/, "plan.err.decimals"],
  [/^Quantities must be zero or more/, "plan.err.negative"],
  [/^Lead time is 0 to 365 days/, "plan.err.leadTime"],
  [/^The supplier must be an active supplier/, "plan.err.supplier"],
  [/^Warehouse not found in the company or inactive/, "po.err.warehouse"],
  [/^The receipt warehouse must count in MRP/, "plan.err.notNettable"],
  [/^Missing permission/, "plan.err.permission"],
  [/^Choose at least one weekday/, "plan.err.weekdays"],
  [/^The horizon is 7 to 730 days/, "plan.err.horizon"],
];

export function planErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}

// ---------------------------------------------------------------------------
// Planned order actions and exceptions (Planning PR 2)
//   firm_planned_order / delete_planned_orders / convert_planned_orders — one RPC each, refused while
//   MRP runs (shared company lock); planning_exception_page — keyset on the column sort (default need date).
// ---------------------------------------------------------------------------

export interface ConvertResult {
  purchase_orders: { id: string; number: string }[];
  production_orders: { id: string; number: string }[];
}

export async function firmPlannedOrder(id: string, qty: string | null, dueDate: string | null) {
  if (isDemoMode) throw new Error("Planning is not available in demo mode");
  const { error } = await db().rpc("firm_planned_order", { p_id: id, p_qty: qty, p_due_date: dueDate });
  if (error) throw error;
}

export async function deletePlannedOrders(ids: string[]) {
  if (isDemoMode) throw new Error("Planning is not available in demo mode");
  const { error } = await db().rpc("delete_planned_orders", { p_ids: ids });
  if (error) throw error;
}

export async function convertPlannedOrders(ids: string[]): Promise<ConvertResult> {
  if (isDemoMode) throw new Error("Planning is not available in demo mode");
  const { data, error } = await db().rpc("convert_planned_orders", { p_ids: ids });
  if (error) throw error;
  return data as ConvertResult;
}

export interface ExceptionRow {
  id: string;
  kind: ExceptionKind;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  source_type: string | null;
  source_id: string | null;
  source_number: string | null;
  need_date: string | null;
  supply_date: string | null;
  qty: string | number | null;
}

export const exceptionKinds: ExceptionKind[] = [
  "expedite",
  "postpone",
  "excess",
  "start_in_past",
  "demand_in_past",
  "below_safety",
  "no_bom",
];
export const isExceptionKind = (v: unknown): v is ExceptionKind => (exceptionKinds as unknown[]).includes(v);

export interface ExceptionListParams {
  companyId: string;
  kind: ExceptionKind | undefined;
  /** Column filters and sort (planning_exception_page spec, planningExceptionListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export async function listExceptions(
  p: ExceptionListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = PLANNED_PAGE,
): Promise<Page<ExceptionRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("planning_exception_page", {
      p_company_id: p.companyId,
      p_kind: p.kind ?? null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (ExceptionRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

export function exceptionListQuery(p: ExceptionListParams) {
  return infiniteQueryOptions({
    queryKey: [...planningKeys.all, "exceptions", p] as const,
    queryFn: ({ pageParam, signal }) => listExceptions(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

const actionRules: [RegExp, string][] = [
  [/^MRP is running for this company/, "plan.err.running"],
  [/^Planned orders changed/, "plan.err.stale"],
  [/^Convert at most 50 production/, "plan.err.tooMany"],
  [/has no supplier/, "plan.err.noSupplier"],
  [/is already converted/, "plan.err.converted"],
  [/^Converted planned orders cannot be deleted/, "plan.err.converted"],
  [/^The quantity must be above zero/, "plan.err.qty"],
  [/^No approved BOM of .* is valid on/, "prd.err.noBom"],
  [/^Missing permission/, "plan.err.actionPermission"],
];

export function planActionErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const rule = actionRules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}

// ---------------------------------------------------------------------------
// Readiness (pegging of the last MRP run) and the live availability check.
//   components    rpc planned_order_components(id) — the order and its planned
//                 sub-assemblies (≤ 8 levels) with each component's supplies;
//                 planning_pegs_demand_idx, one round-trip, ~1–10 KB.
//   availability  rpc planned_order_availability(id) — free stock now per
//                 component of the BOM (inventory_balances / reservations keys).
// ---------------------------------------------------------------------------

export interface PegSupply {
  kind: "stock" | "order" | "planned" | "short";
  type: string | null;
  id: string | null;
  date: string | null;
  qty: number | string;
  late: boolean | null;
  number: string | null;
  planned_kind: PlannedOrderKind | null;
  planned_readiness: PlannedReadiness | null;
  planned_status: PlannedOrderStatus | null;
}

export interface PegComponent {
  item_id: string;
  item_code: string;
  item_description: string;
  uom: string;
  uom_decimals: number;
  need_date: string;
  required: number | string;
  /** false: MRP does not plan the item (planning method none) — no pegging, check stock. */
  planned_by_mrp: boolean;
  stock: number | string;
  ordered: number | string;
  planned: number | string;
  short: number | string;
  supplies: PegSupply[];
}

export interface PegOrder {
  id: string;
  parent_id: string | null;
  depth: number;
  kind: PlannedOrderKind;
  status: PlannedOrderStatus;
  item_id: string;
  item_code: string;
  item_description: string;
  qty: number | string;
  uom: string;
  uom_decimals: number;
  start_date: string;
  due_date: string;
  readiness: PlannedReadiness | null;
  material_date: string | null;
  components: PegComponent[];
}

export interface PlannedComponents {
  run_id: string | null;
  run_at: string | null;
  orders: PegOrder[];
}

export interface Availability {
  checked_at: string;
  has_bom: boolean;
  ready: boolean;
  components: {
    item_id: string;
    item_code: string;
    item_description: string;
    uom: string;
    uom_decimals: number;
    required: number | string;
    free: number | string;
    short: number | string;
  }[];
}

export function plannedComponentsQuery(id: string) {
  return queryOptions({
    queryKey: [...planningKeys.all, "components", id] as const,
    queryFn: async ({ signal }): Promise<PlannedComponents> => {
      if (isDemoMode) return { run_id: null, run_at: null, orders: [] };
      const { data, error } = await db().rpc("planned_order_components", { p_id: id }).abortSignal(signal);
      if (error) throw error;
      return data as PlannedComponents;
    },
    staleTime: 60_000,
  });
}

export async function checkAvailability(id: string): Promise<Availability> {
  if (isDemoMode) throw new Error("Planning is not available in demo mode");
  const { data, error } = await db().rpc("planned_order_availability", { p_id: id });
  if (error) throw error;
  return data as Availability;
}
